import { Calendar, Clipboard, Hammer, Handshake, Kanban, Key, Megaphone, Package, Paintbrush, Sparkles, Star, Truck, Users, Wrench, type LucideIcon } from "lucide-react";
import boardsJson from "@shared/boards.json";
import { api } from "./api";
import type { Board, BoardCard, BoardColumn, BoardSummary } from "./types";

// Eigen borden (contract §6.9, fase 9). De keuzes (icoontjes, standaardkolommen, limieten) komen uit
// shared/boards.json, zodat de frontend dezelfde lijst toont die de backend controleert.

export const BOARDS = boardsJson as { icons: string[]; defaultColumns: string[]; limits: { boards: number; columns: number; cards: number } };

const ICONS: Record<string, LucideIcon> = {
  kanban: Kanban,
  hammer: Hammer,
  wrench: Wrench,
  paintbrush: Paintbrush,
  truck: Truck,
  package: Package,
  sparkles: Sparkles,
  key: Key,
  clipboard: Clipboard,
  users: Users,
  handshake: Handshake,
  calendar: Calendar,
  megaphone: Megaphone,
  star: Star
};

export const ICON_LABELS: Record<string, string> = {
  kanban: "Bord",
  hammer: "Hamer",
  wrench: "Moersleutel",
  paintbrush: "Penseel",
  truck: "Vrachtwagen",
  package: "Pakket",
  sparkles: "Schoonmaak",
  key: "Sleutel",
  clipboard: "Klembord",
  users: "Mensen",
  handshake: "Handdruk",
  calendar: "Kalender",
  megaphone: "Megafoon",
  star: "Ster"
};

export const boardIcon = (name: string): LucideIcon => ICONS[name] ?? Kanban;

/** Wat de kaart-API's aanvaarden; "" of null wist een veld. */
export interface CardInput {
  columnId?: string;
  title?: string;
  description?: string | null;
  assigneeId?: string | null;
  dueDate?: string | null;
  propertyId?: string | null;
  companyId?: string | null;
  contactId?: string | null;
  archived?: boolean;
}

export const boardApi = {
  list: (archived = false) => api<{ items: BoardSummary[] }>(`boards${archived ? "?archived=true" : ""}`),
  get: (id: string, archived = false) => api<Board>(`boards/${id}${archived ? "?archived=true" : ""}`),
  create: (input: { name: string; icon: string; columns: string[] }) => api<Board>("boards", { body: input }),
  update: (id: string, input: { name?: string; icon?: string; archived?: boolean }) => api<Board>(`boards/${id}`, { method: "PATCH", body: input }),
  move: (id: string, beforeId: string | null) => api<{ order: string[] }>(`boards/${id}/move`, { body: { beforeId } }),
  addColumn: (boardId: string, name: string) => api<BoardColumn>(`boards/${boardId}/columns`, { body: { name } }),
  updateColumn: (boardId: string, columnId: string, input: { name?: string; archived?: boolean }) => api<BoardColumn>(`boards/${boardId}/columns/${columnId}`, { method: "PATCH", body: input }),
  moveColumn: (boardId: string, columnId: string, beforeId: string | null) => api<{ columns: BoardColumn[] }>(`boards/${boardId}/columns/${columnId}/move`, { body: { beforeId } }),
  addCard: (boardId: string, input: CardInput) => api<BoardCard>(`boards/${boardId}/cards`, { body: input }),
  updateCard: (id: string, input: CardInput) => api<BoardCard>(`cards/${id}`, { method: "PATCH", body: input }),
  moveCard: (id: string, columnId: string, beforeId: string | null) => api<{ card: BoardCard; positions: Record<string, number> }>(`cards/${id}/move`, { body: { columnId, beforeId } })
};

/** Kaarten in volgorde (zoals de server ze sorteert). */
export const byPosition = <T extends { position: number }>(list: T[]) => [...list].sort((a, b) => a.position - b.position);
